import React, { useRef, useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useDatePickerInput } from '@/components/atoms/DatePickerInput/datePickerInput-hook'
import { DATE_VALIDATION_MESSAGE } from '@/utils/constants/commonConstants'
import { Icon } from "@/components/atoms"
import "./DatePickerInput.scss"

export default function DatePickerInput ( {
  value,
  updateValue,
  uiSchema,
  id = ''
} ) {
  const {
    label,
    placeholder = 'mm/dd/yyyy',
    required = true,
    className,
    validationRules = [],
    disabled = false,
    disabledHelperText = DATE_VALIDATION_MESSAGE.END_DATE_HELPER_TEXT,
    dependentDate,
  } = uiSchema

  const { validateDates } = useDatePickerInput(label, validationRules)
  const ref = useRef()
  const [isError, setIsError] = useState(false)
  const [helperText, setHelperText] = useState('')

  const minDate = ( () => {
    const d = new Date()
    d.setFullYear( d.getFullYear() - 30 )
    return d
  } )()
  const maxDate = ( () => {
    const d = new Date()
    d.setFullYear( d.getFullYear() + 200 )
    return d
  } )()

  useEffect(() => {
    const lastElement = ref.current?.lastChild?.lastChild
    if (!lastElement) return

    const config = { attributes: true, childList: true, subtree: true }
    const callback = mutationList => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes') {
          lastElement.style.display = lastElement.style.opacity === '0' ? 'none' : 'block'
        }
      }
    }
    const observer = new MutationObserver(callback)
    observer.observe(lastElement, config)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if ((dependentDate || value) && !disabled) {
      handleStartDateEndDateValidation(value)
    } else if (!value) {
      clearValidationError(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentDate, value])

  const handleStartDateEndDateValidation = val => {
    const errorMsg = validateDates(val)
    if (errorMsg) {
      handleValidationError(errorMsg, val)
    } else {
      clearValidationError(val)
    }
  }

  const handleValidationError = (errorMsg, val) => {
    setHelperText(errorMsg)
    updateValue(val, true)
    setIsError(true)
  }

  const clearValidationError = val => {
    updateValue(val, false)
    setIsError(false)
    setHelperText('')
  }

  const onChangeVal = val => {
    if (required && !val) {
      setIsError(true)
      setHelperText('Date is required')
      return
    }
    handleStartDateEndDateValidation(val)
  }

  // Helper to parse and format date as MM/DD/YYYY
  const parseDate = ( dateStr ) => {
    if ( !dateStr ) return null
    const [ month, day, year ] = dateStr.split( '/' )
    return new Date( year, month - 1, day )
  }
  const formatDate = ( dateObj ) => {
    if ( !dateObj ) return ''
    const mm = String( dateObj.getMonth() + 1 ).padStart( 2, '0' )
    const dd = String( dateObj.getDate() ).padStart( 2, '0' )
    const yyyy = dateObj.getFullYear()
    return `${ mm }/${ dd }/${ yyyy }`
  }

  const SingleDateCustomInput = React.forwardRef(
    ( { value = '', onClick, placeholder, isError, disabled }, ref ) => (
      <button
        type="button"
        onClick={ e => {
          e.preventDefault()
          e.stopPropagation()
          onClick && onClick( e )
        } }
        ref={ ref }
        disabled={ disabled }
        className={ `datepicker-input-btn flex items-center justify-between w-full border rounded text-left ${ disabled ? 'datepicker-input-btn--disabled' : '' } ${ isError ? 'datepicker-input-btn--error' : '' }` }
      >
        <span
          className={ `datepicker-input-btn__text ${ value ? 'datepicker-input-btn__text--filled' : 'datepicker-input-btn__text--placeholder'
            }` }
        >
          { value || placeholder || 'Choose date' }
        </span>
        <span className="datepicker-input-btn__icon">
          <Icon icon="calendar_today" size="small" />
        </span>
      </button>
    )
  )

   let helperClass = 'datepicker-helper flex items-center gap-1 mt-1'
   if (isError && helperText?.length > 0) {
     helperClass += ' datepicker-helper--error'
   } else if (disabled) {
     helperClass += ' datepicker-helper--disabled'
   }

   return (
     <div ref={ref}>
      <label
        className="datepicker-label block mb-1"
      >
        { label } { required ? '*' : '' }
       </label>
       <DatePicker
         id={id ? 'date_picker_input_' + id : 'date_picker_input'}
         selected={parseDate(value)}
         onChange={date => {
           if (disabled) return
          onChangeVal( formatDate( date ) )
         }}
        minDate={ minDate }
        maxDate={ maxDate }
         dateFormat="MM/dd/yyyy"
        placeholderText={ placeholder }
        className={ className }
        disabled={ disabled }
         peekNextMonth
         showMonthDropdown
         useShortMonthInDropdown
         showYearDropdown
         dateFormatCalendar="MMMM"
        yearDropdownItemNumber={ 15 }
         scrollableYearDropdown
         dropdownMode="select"
         popperClassName="custom-datepicker-popper"
         customInput={
           <SingleDateCustomInput
            placeholder={ placeholder }
            value={ value }
            isError={ !!( isError && helperText ) }
            disabled={ disabled }
           />
         }
       />
       <div className={helperClass} data-testid={id ? 'date_picker_input_helper_' + id : 'date_picker_input_helper'}>
         {isError && helperText?.length > 0 ? (
           <>

             <div className="datepicker-helper__text datepicker-helper__text--error datepicker-helper__text--disabled-flex">
               <Icon alt="" icon="info" size="small" />
              { helperText }
             </div>
           </>
        ) : disabled && disabledHelperText  ? (

           <div className="datepicker-helper__text datepicker-helper__text--disabled datepicker-helper__text--disabled-flex">
             <Icon alt="" icon="info" size="small" className="datepicker-helper__icon--inline" />
              { disabledHelperText }
           </div>
         ) : (
           helperText
        ) }
       </div>
     </div>
   )
}
